import React, { useEffect, useState } from 'react';
import { Check, Clipboard, Download } from 'lucide-react';
import type { Components } from 'react-markdown';
import { cn } from 'lib/utils';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import dark from 'react-syntax-highlighter/dist/esm/styles/prism/atom-dark';
import light from 'react-syntax-highlighter/dist/esm/styles/prism/prism';

type SyntaxHighlighterTheme = Record<string, React.CSSProperties>;

const prismDarkTheme = dark as unknown as SyntaxHighlighterTheme;
const prismLightTheme = light as unknown as SyntaxHighlighterTheme;

type MarkdownCodeProps = React.ComponentPropsWithoutRef<'code'> & {
  inline?: boolean;
  node?: unknown;
};

const MarkdownCode = ({
  inline,
  className,
  children,
  style: codeStyle,
  ...props
}: MarkdownCodeProps) => {
  const match = /language-(\w+)/.exec(className || '');
  const code = String(children).replace(/\n$/, '');
  const [copied, setCopied] = useState(false);
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkTheme = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Treat as block code if:
  // 1. Not inline (undefined or false), AND
  // 2. Either has a language class (from ```language) OR contains newlines (multi-line)
  const isBlockCode = !inline && (match !== null || code.includes('\n'));

  if (isBlockCode) {
    const language = match ? match[1] : '';

    return (
      <div className="min-w-0 max-w-full overflow-auto rounded-md border bg-muted">
        <div className="flex w-full items-center justify-between border-b bg-muted/70 p-2.5 text-xs text-muted-foreground">
          <span className="text-sm uppercase">{language || ''}</span>
          <button
            onClick={handleCopy}
            className="flex items-center gap-1.5 rounded px-2 py-1 text-xs hover:bg-accent hover:text-accent-foreground"
            title="Copy code"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                <span>Copied</span>
              </>
            ) : (
              <>
                <Clipboard className="h-4 w-4" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
        <SyntaxHighlighter
          showLineNumbers
          style={isDark ? prismDarkTheme : prismLightTheme}
          customStyle={{
            margin: 0,
            padding: '1rem',
            background: 'hsl(var(--surface))',
            color: 'hsl(var(--foreground))',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            borderRadius: '0 0 0.375rem 0.375rem',
            scrollbarColor: 'hsl(var(--border)) transparent',
            scrollMargin: '0',
            maxWidth: '100%',
            ...(codeStyle || {}),
          }}
          language={language || 'text'}
          PreTag="div"
          {...props}
        >
          {code}
        </SyntaxHighlighter>
      </div>
    );
  }

  return (
    <code
      className={cn(
        'rounded-[7px] border border-gray-300/60 bg-[#f0f1f3] px-1.5 py-0.5 font-mono text-sm text-[#ab5152] dark:border-gray-600 dark:bg-muted dark:text-[#fca5a5]',
        className
      )}
      {...props}
    >
      {children}
    </code>
  );
};

const MarkdownImage: Components['img'] = ({ src, alt, ...props }) => {
  const [downloaded, setDownloaded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const handleDownload = async () => {
    if (!src) return;

    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const filename = alt || 'image';
      link.download = `${filename}.png`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setDownloaded(true);
      setTimeout(() => setDownloaded(false), 2000);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  return (
    <div className="max-w-lg rounded-lg border overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-card ">
        <span className="text-sm text-high-emphasis font-medium truncate">{alt || 'Image'}</span>
        <button
          onClick={handleDownload}
          className="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs bg-white hover:cursor-pointer border border-gray-300  ml-2 flex-shrink-0"
          title="Download image"
        >
          {downloaded ? (
            <>
              <Check className="h-3.5 w-3.5 text-green-600" />
              <span className="text-high-emphasis">Downloaded</span>
            </>
          ) : (
            <>
              <Download className="h-3.5 w-3.5 text-high-emphasis" />
              <span className="text-high-emphasis">Download</span>
            </>
          )}
        </button>
      </div>
      <div className="bg-white relative min-h-[300px] sm:min-h-[400px] md:min-h-[512px] flex items-center justify-center">
        {!isLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-muted/50 animate-pulse">
            <div className="text-muted-foreground text-sm">Loading image...</div>
          </div>
        )}
        <img
          loading="eager"
          alt={alt}
          src={src}
          className="h-auto max-w-[512px] w-full my-0 object-contain"
          onLoad={() => setIsLoaded(true)}
          style={{ opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s ease-in-out' }}
          {...props}
        />
      </div>
    </div>
  );
};

export const MarkdownComponentsMap: Partial<Components> = {
  p: (props) => {
    const isElementWithSrc = (
      child: React.ReactNode
    ): child is React.ReactElement<{ src?: string }> =>
      React.isValidElement(child) &&
      typeof (child as React.ReactElement<{ src?: unknown }>).props?.src === 'string';

    const hasImage = React.Children.toArray(props.children).some((child) =>
      isElementWithSrc(child)
    );
    if (hasImage) {
      return <div className="break-words leading-relaxed">{props.children}</div>;
    }
    return (
      <p className="break-words leading-relaxed [overflow-wrap:anywhere]">{props.children}</p>
    );
  },

  a: (props) => (
    <a
      className="text-primary dark:text-[#ffffff] break-all [overflow-wrap:anywhere]"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {props.children}
    </a>
  ),

  strong: (props) => <strong>{props.children}</strong>,
  em: (props) => <em>{props.children}</em>,
  del: (props) => <del>{props.children}</del>,

  ul: (props) => (
    <ul className="my-2 list-disc space-y-1 pl-6 [list-style-position:outside] [&_ul]:mt-1 [&_ul]:pl-5">
      {props.children}
    </ul>
  ),
  ol: (props) => (
    <ol className="my-2 list-decimal space-y-1 pl-6 [list-style-position:outside] [&_ol]:mt-1 [&_ol]:pl-5">
      {props.children}
    </ol>
  ),
  li: (props) => (
    <li className="break-words leading-relaxed [overflow-wrap:anywhere] [&>p]:m-0 [&>p+p]:mt-2 [&>ul]:mt-1 [&>ol]:mt-1">
      {props.children}
    </li>
  ),

  table: (props) => (
    <div className="my-1.5 min-w-0 max-w-full overflow-x-auto [scrollbar-width:thin] [scrollbar-color:hsl(var(--border)/0.65)_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border/70">
      <table className="w-max min-w-full border-collapse border-[1.5px] !border-border !mb-0 !mt-0">
        {props.children}
      </table>
    </div>
  ),

  th: (props) => (
    <th className="min-w-[100px] md:min-w-[150px] break-all border p-2">{props.children}</th>
  ),
  td: (props) => (
    <td className="min-w-[100px] md:min-w-[150px] break-words border p-2">{props.children}</td>
  ),

  blockquote: (props) => (
    <blockquote className="break-words py-2 border-l-4 border-border pl-4 text-foreground/90 [&>p]:m-0 [&>p+p]:mt-3">
      {props.children}
    </blockquote>
  ),

  code: MarkdownCode as Components['code'],

  pre: (props) => <pre className="overflow-x-auto p-0">{props.children}</pre>,

  img: MarkdownImage,
};
